import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
  TextField,
  Chip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { Expense, Debt, ExpenseSection } from "../lib/types";
import { expenseUtils } from "../lib";
import { pageShareAPI, PageShareInfo } from "../services/api/api";
import { SearchFilterBar } from "../components/common";
import ExpenseList from "../components/expenses/ExpenseList";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#0f0f0f",
      paper: "#1a1a1a",
    },
    primary: {
      main: "#3182CE",
    },
  },
});

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Math.abs(amount));

const formatDate = (value?: Date | string | null): string => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { timeZone: "UTC" });
};

const SharedExpensesPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const [shareInfo, setShareInfo] = useState<PageShareInfo | null>(null);
  const [password, setPassword] = useState("");
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [expenseShareSettings, setExpenseShareSettings] = useState<{
    hiddenExpenseIds?: string[];
    hiddenSections?: ExpenseSection[];
  }>({ hiddenExpenseIds: [], hiddenSections: [] });
  const [isLive, setIsLive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [paidFilter, setPaidFilter] = useState<"all" | "paid" | "unpaid">(
    "all"
  );
  const [recurringFilter, setRecurringFilter] = useState<
    "all" | "recurring" | "one-time"
  >("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");

  const handleSearchApply = useCallback(() => {
    setAppliedSearchQuery(searchQuery);
    console.debug("[SharedExpenses] Search applied:", searchQuery);
  }, [searchQuery]);

  const loadShareInfo = useCallback(async () => {
    if (!shareId) {
      setError("Invalid share link");
      setLoading(false);
      return;
    }

    try {
      console.debug("[SharedExpenses] Loading share info:", shareId);
      const info = await pageShareAPI.getInfo(shareId);
      if (info.pageType !== "expenses") {
        setError("This share link is not for expenses.");
        setShareInfo(null);
        return;
      }
      setShareInfo(info);
      console.debug("[SharedExpenses] Share info loaded:", info);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load share");
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  const validateShare = useCallback(
    async (providedPassword?: string) => {
      if (!shareId) return;
      setValidating(true);
      setError(null);
      try {
        console.debug("[SharedExpenses] Validating share:", shareId);
        const result = await pageShareAPI.validate(shareId, providedPassword);
        setExpenses(result.expenses || []);
        setDebts(result.debts || []);
        setExpenseShareSettings(result.payload?.expenseSettings || {});
        setIsLive(result.isLive);
        setIsAuthenticated(true);
        console.debug("[SharedExpenses] Share validated:", {
          expenses: result.expenses?.length || 0,
          debts: result.debts?.length || 0,
          isLive: result.isLive,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Validation failed";
        setError(message);
        console.error("[SharedExpenses] Validation failed:", message);
      } finally {
        setValidating(false);
      }
    },
    [shareId]
  );

  useEffect(() => {
    loadShareInfo();
  }, [loadShareInfo]);

  useEffect(() => {
    if (!shareInfo) return;
    if (shareInfo.requiresPassword) return;
    validateShare();
  }, [shareInfo, validateShare]);

  const handleSubmitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password.trim()) return;
    await validateShare(password.trim());
  };

  const categories = useMemo(() => {
    const values = new Set<string>();
    expenses.forEach((expense) => {
      if (expense.internalCategory) {
        values.add(expense.internalCategory);
      }
    });
    return ["all", ...Array.from(values).sort()];
  }, [expenses]);

  const vendors = useMemo(() => {
    const values = new Set<string>();
    expenses.forEach((expense) => {
      if (expense.vendor) values.add(expense.vendor);
    });
    return ["all", ...Array.from(values).sort()];
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    const query = appliedSearchQuery.trim().toLowerCase();
    const hiddenIds = new Set(expenseShareSettings.hiddenExpenseIds || []);
    const matchesQuery = (expense: Expense) => {
      if (!query) return true;
      return [
        expense.name,
        expense.vendor,
        expense.internalCategory,
        expense.notes,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(query));
    };

    const matchesPaid = (expense: Expense) => {
      if (paidFilter === "all") return true;
      return paidFilter === "paid" ? expense.isPaid : !expense.isPaid;
    };

    const matchesRecurring = (expense: Expense) => {
      if (recurringFilter === "all") return true;
      return recurringFilter === "recurring"
        ? expense.isRecurring
        : !expense.isRecurring;
    };

    const matchesCategory = (expense: Expense) => {
      if (categoryFilter === "all") return true;
      return expense.internalCategory === categoryFilter;
    };

    const matchesVendor = (expense: Expense) => {
      if (vendorFilter === "all") return true;
      return expense.vendor === vendorFilter;
    };

    const next = expenses.filter(
      (expense) =>
        !hiddenIds.has(expense.id) &&
        matchesQuery(expense) &&
        matchesPaid(expense) &&
        matchesRecurring(expense) &&
        matchesCategory(expense) &&
        matchesVendor(expense)
    );

    console.debug("[SharedExpenses] Filtered expenses:", {
      total: expenses.length,
      filtered: next.length,
    });

    return next;
  }, [
    expenses,
    appliedSearchQuery,
    paidFilter,
    recurringFilter,
    categoryFilter,
    vendorFilter,
    expenseShareSettings.hiddenExpenseIds,
  ]);

  // Categorize filtered expenses into sections for ExpenseList
  const categorizedExpenses = useMemo(() => {
    const base = expenseUtils.categorizeExpenses(filteredExpenses);
    const hiddenSections = expenseShareSettings.hiddenSections || [];
    const next = { ...base };
    hiddenSections.forEach((section) => {
      next[section] = [];
    });
    return next;
  }, [filteredExpenses, expenseShareSettings.hiddenSections]);

  const sectionTotals = useMemo(
    () => expenseUtils.calculateSectionTotals(categorizedExpenses, debts),
    [categorizedExpenses, debts]
  );

  const totals = useMemo(() => {
    const total = filteredExpenses.reduce(
      (sum, expense) => sum + Number(expense.amount || 0),
      0
    );
    const paid = filteredExpenses
      .filter((expense) => expense.isPaid)
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    return {
      total,
      paid,
      remaining: total - paid,
    };
  }, [filteredExpenses]);

  // Compute date range from expenses
  const dateRange = useMemo(() => {
    if (filteredExpenses.length === 0) return null;
    let earliest: Date | null = null;
    let latest: Date | null = null;
    filteredExpenses.forEach((expense) => {
      if (!expense.dueDate) return;
      const d =
        expense.dueDate instanceof Date
          ? expense.dueDate
          : new Date(expense.dueDate);
      if (Number.isNaN(d.getTime())) return;
      if (!earliest || d < earliest) earliest = d;
      if (!latest || d > latest) latest = d;
    });
    if (!earliest || !latest) return null;
    return { earliest, latest };
  }, [filteredExpenses]);

  // No-op callbacks for read-only ExpenseList
  const noop = useCallback(() => {}, []);
  const noopMarkPaid = useCallback(async () => {}, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0f0f0f", p: 3 }}>
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" sx={{ color: "white", mb: 0.5 }}>
              Shared Expenses
            </Typography>
            {dateRange && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                {formatDate(dateRange.earliest)} —{" "}
                {formatDate(dateRange.latest)}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {isLive && (
                <Chip label="Live data" color="primary" size="small" />
              )}
              {shareInfo?.requiresPassword && (
                <Chip label="Password" color="warning" size="small" />
              )}
              {shareInfo?.requiresAuth && (
                <Chip label="Auth required" color="secondary" size="small" />
              )}
              {shareInfo?.expiresAt && (
                <Chip
                  label={`Expires ${formatDate(shareInfo.expiresAt)}`}
                  size="small"
                />
              )}
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : shareInfo?.requiresPassword && !isAuthenticated ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <Card sx={{ maxWidth: 420, width: "100%" }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <LockIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ textAlign: "center" }}>
                      Password Protected
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mt: 1,
                      }}
                    >
                      Enter the password to view these expenses.
                    </Typography>
                  </Box>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  <form onSubmit={handleSubmitPassword}>
                    <TextField
                      fullWidth
                      type="password"
                      label="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{ mb: 2 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={validating || !password.trim()}
                    >
                      {validating ? (
                        <CircularProgress size={24} />
                      ) : (
                        "View Expenses"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </Box>
          ) : error && !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {error}
              </Alert>
            </Box>
          ) : !isAuthenticated ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Box sx={{ maxWidth: 480, textAlign: "center" }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This link requires authentication. Please log in to view these
                  expenses.
                </Alert>
                <Button variant="contained" href="/login">
                  Log In
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 2,
                  mb: 3,
                }}
              >
                {[
                  {
                    label: "Total Expenses",
                    value: formatCurrency(totals.total),
                  },
                  { label: "Paid", value: formatCurrency(totals.paid) },
                  {
                    label: "Remaining",
                    value: formatCurrency(totals.remaining),
                  },
                ].map((card) => (
                  <Card key={card.label}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="h5" sx={{ mt: 0.5 }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <SearchFilterBar
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                onSearchApply={handleSearchApply}
                searchPlaceholder="Search expenses"
                filters={[
                  {
                    key: "paid",
                    label: "Paid",
                    value: paidFilter,
                    onChange: (value) =>
                      setPaidFilter(value as "all" | "paid" | "unpaid"),
                    options: [
                      { value: "all", label: "All" },
                      { value: "paid", label: "Paid" },
                      { value: "unpaid", label: "Unpaid" },
                    ],
                  },
                  {
                    key: "recurring",
                    label: "Recurring",
                    value: recurringFilter,
                    onChange: (value) =>
                      setRecurringFilter(
                        value as "all" | "recurring" | "one-time"
                      ),
                    options: [
                      { value: "all", label: "All" },
                      { value: "recurring", label: "Recurring" },
                      { value: "one-time", label: "One-time" },
                    ],
                  },
                  {
                    key: "category",
                    label: "Category",
                    value: categoryFilter,
                    onChange: setCategoryFilter,
                    options: categories.map((value) => ({
                      value,
                      label: value === "all" ? "All" : value,
                    })),
                  },
                  {
                    key: "vendor",
                    label: "Vendor",
                    value: vendorFilter,
                    onChange: setVendorFilter,
                    options: vendors.map((value) => ({
                      value,
                      label: value === "all" ? "All" : value,
                    })),
                  },
                ]}
              />

              {filteredExpenses.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No expenses match this view.
                </Alert>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <ExpenseList
                    expenses={categorizedExpenses}
                    sectionTotals={sectionTotals}
                    debts={debts}
                    allExpanded={false}
                    expandedSection="expected"
                    readOnly={true}
                    onEditExpense={noop}
                    onDeleteExpense={noop}
                    onMarkAsPaid={noopMarkPaid}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SharedExpensesPage;
